import { cn } from '@/utils/cn';
import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';

export type GridVariant = 'default' | '1-or-full';

export default function Grid({
    className = null,
    minColumns = 0,
    maxColumns = 4,
    variant = 'default',
    children,
}: {
    className?: string | null;
    minColumns?: number;
    maxColumns?: number;
    variant?: GridVariant;
    children: ReactNode;
}) {
    const normalize = (value: number) => Math.min(4, Math.max(1, value));
    const min = normalize(minColumns);
    const max = normalize(maxColumns);

    const baseCols = ['grid-cols-1', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4'] as const;
    const smCols = ['sm:grid-cols-1', 'sm:grid-cols-2', 'sm:grid-cols-3', 'sm:grid-cols-4'] as const;
    const mdCols = ['md:grid-cols-1', 'md:grid-cols-2', 'md:grid-cols-3', 'md:grid-cols-4'] as const;
    const xlCols = ['xl:grid-cols-1', 'xl:grid-cols-2', 'xl:grid-cols-3', 'xl:grid-cols-4'] as const;
    const pick = (cols: readonly [string, string, string, string], n: number) => cols[n - 1];

    const variants = cva('grid', {
        variants: {
            variant: {
                'always-1': 'grid-cols-1',
                '1-or-full': cn(
                    'grid-cols-1',
                    max === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : pick(mdCols, max)
                ),
                default: cn(
                    'w-full',
                    pick(baseCols, min),
                    max > 1 && pick(smCols, min),
                    max === 4 && 'sm:grid-cols-2',
                    max > 1 && pick(xlCols, max)
                ),
            },
        },
    });

    return (
        <div
            className={cn('grid', variants({ variant }), 'gap-x-6 gap-y-4 md:gap-y-12', className)}
        >
            {children}
        </div>
    );
}
